import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import { getMovieById, getCategoryById, getCategoryName, getRelatedMovies, formatViews } from "@/utils/movie";
import { useFavorites } from "@/hooks/useFavorites";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export default function Detail() {
  const { id = "" } = useParams();
  const movie = getMovieById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  useDocumentTitle(
    movie ? `${movie.title} - 三级影片详情 - 三级片电影` : "影片未找到 - 三级片电影",
    movie ? `${movie.title}：${movie.synopsis.slice(0, 60)}…` : undefined
  );

  if (!movie) {
    return (
      <SiteLayout>
        <div className="mx-auto w-full max-w-3xl px-4 md:px-6 py-24 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">未找到该影片</h1>
          <p className="text-sm text-foreground-500 mb-6">这部影片可能已被移除，去首页看看其他推荐吧。</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-foreground-950 hover:bg-primary-600 transition-colors"
          >
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const category = getCategoryById(movie.categoryId);
  const related = getRelatedMovies(movie, 6);
  const favorite = isFavorite(movie.id);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2200);
  };

  const handleFavorite = () => {
    const nowFavorite = toggleFavorite(movie.id);
    showToast(nowFavorite ? "已加入收藏" : "已取消收藏");
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast("链接已复制到剪贴板");
    } catch {
      showToast("复制失败，请手动复制地址栏链接");
    }
  };

  const infoItems = [
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.cast.join(" / ") },
    { label: "上映时间", value: `${movie.year} 年` },
    { label: "片长", value: `${movie.duration} 分钟` },
    { label: "地区", value: movie.region },
    { label: "类型", value: getCategoryName(movie.categoryId) },
  ];

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-6 pt-6 md:pt-8 pb-24 lg:pb-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: getCategoryName(movie.categoryId), to: `/category/${movie.categoryId}` },
            { label: movie.title },
          ]}
        />

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 md:gap-10">
          <div className="mx-auto w-40 sm:w-56 lg:w-full lg:mx-0">
            <div className="relative w-full aspect-[2/3] overflow-hidden rounded-lg border border-background-200/70 bg-background-200">
              <img
                src={movie.cover}
                alt={`${movie.title} 影片封面`}
                title={`${movie.title} - 三级影片`}
                className="absolute inset-0 h-full w-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex flex-col">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">{movie.title}</h1>
            <div className="flex flex-wrap items-center gap-2 mb-5">
              {movie.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="rounded-full bg-secondary-500/20 px-3 py-1 text-xs text-secondary-300 hover:bg-accent-500/20 hover:text-accent-500 transition-colors"
                >
                  {tag}
                </Link>
              ))}
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 rounded-lg border border-background-200/70 bg-background-100 p-4 md:p-5">
              {infoItems.map((item) => (
                <div key={item.label} className="flex gap-3 text-sm">
                  <dt className="shrink-0 w-16 text-foreground-500">{item.label}</dt>
                  <dd className="text-foreground-800">{item.value}</dd>
                </div>
              ))}
              <div className="flex gap-3 text-sm">
                <dt className="shrink-0 w-16 text-foreground-500">评分</dt>
                <dd className="flex items-center gap-1 text-foreground-800">
                  <i className="ri-star-fill text-accent-500" />
                  {movie.rating.toFixed(1)}
                  <span className="text-foreground-500">·</span>
                  <i className="ri-play-circle-line" />
                  {formatViews(movie.views)} 次播放
                </dd>
              </div>
            </dl>

            {category && (
              <div className="mt-5">
                <Link
                  to={`/category/${category.id}`}
                  className="inline-flex items-center gap-2 rounded-md border border-background-300/60 bg-background-100 px-4 py-2 text-sm text-foreground-700 hover:border-accent-500 hover:text-accent-500 transition-colors"
                >
                  <i className={category.icon} />
                  所属分类：{category.name}
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            )}

            <div className="mt-6 hidden lg:flex items-center gap-3">
              <button
                type="button"
                onClick={handleFavorite}
                className={`inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-medium transition-colors whitespace-nowrap ${
                  favorite
                    ? "bg-accent-500 text-foreground-950 hover:bg-accent-600"
                    : "bg-primary-500 text-foreground-950 hover:bg-primary-600"
                }`}
              >
                <i className={`${favorite ? "ri-heart-fill" : "ri-heart-line"}`} />
                {favorite ? "已收藏" : "收藏影片"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center gap-2 rounded-md border border-background-300/60 bg-background-100 px-5 py-2.5 text-sm font-medium text-foreground-700 hover:border-accent-500 hover:text-accent-500 transition-colors whitespace-nowrap"
              >
                <i className="ri-share-forward-line" />
                分享链接
              </button>
            </div>
          </div>
        </div>

        <section className="mt-10 md:mt-12">
          <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-3">剧情介绍</h2>
          <p className="text-sm md:text-base leading-relaxed text-foreground-600">{movie.synopsis}</p>
          <p className="mt-3 text-sm leading-relaxed text-foreground-500">
            《{movie.title}》由 {movie.director} 执导，{movie.cast.join("、")} 主演，{movie.year} 年于
            {movie.region}上映，片长 {movie.duration} 分钟。影片以
            {movie.tags.join("、")}为核心元素，呈现了一段值得回味的银幕故事。
          </p>
        </section>

        {related.length > 0 && (
          <section className="mt-12 md:mt-16">
            <div className="flex items-end justify-between mb-5 md:mb-6">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">相关推荐</h2>
                <p className="mt-1 text-sm text-foreground-500">根据影片分类与标签为你推荐的相似作品</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
              {related.map((item) => (
                <MovieCard key={item.id} movie={item} />
              ))}
            </div>
          </section>
        )}
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-background-200/70 bg-background-100/95 backdrop-blur-md px-4 py-3 lg:hidden">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleFavorite}
            className={`flex flex-1 items-center justify-center gap-2 rounded-md py-2.5 text-sm font-medium transition-colors whitespace-nowrap ${
              favorite
                ? "bg-accent-500 text-foreground-950 hover:bg-accent-600"
                : "bg-primary-500 text-foreground-950 hover:bg-primary-600"
            }`}
          >
            <i className={`${favorite ? "ri-heart-fill" : "ri-heart-line"}`} />
            {favorite ? "已收藏" : "收藏"}
          </button>
          <button
            type="button"
            onClick={handleShare}
            className="flex flex-1 items-center justify-center gap-2 rounded-md border border-background-300/60 bg-background-50 py-2.5 text-sm font-medium text-foreground-700 hover:border-accent-500 hover:text-accent-500 transition-colors whitespace-nowrap"
          >
            <i className="ri-share-forward-line" />
            分享
          </button>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-24 lg:bottom-8 left-1/2 -translate-x-1/2 z-50 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-foreground-950 fade-in">
          {toast}
        </div>
      )}
    </SiteLayout>
  );
}